'use client';
import { useState } from 'react';
import cn from 'classnames';
import Image from 'next/image';

import Text from '@/components/common/Text/Text';
import { useColorSwatches } from '@/components/hook/colorSwatches';

import ChevronDown from 'public/img/icon/chevron-down-black.svg';

import { FilterState } from './ProductListApp';
import { createFilterStateCopy } from './dataTools';
import { DropdownState } from './FilterMenu';

type FacetMenuProps = {
  className?: string;
  filterState: FilterState;
  dropdownStateKey: keyof DropdownState;
  stateKey: 'brand' | 'color' | 'occasion' | 'material' | 'influencers';
  facets?: { [displayValue: string]: number };
  title: string;
  open: boolean;
  onFilterOpen: (stateKey: keyof DropdownState) => void;
  onFacetClick: (newState: FilterState) => void;
  checkmarkComponent: JSX.Element;
  /**
   * Facet values that are pinned and non-removable (e.g. a campaign's Occasion).
   * A locked value always renders, always shows as checked, and clicking it is a
   * no-op — so a campaign occasion stays applied even if a user tries to clear it,
   * and remains visible even when the live search returns no count for it.
   */
  lockedValues?: string[];
};

function ColorSwatch({ name }: { name: string }) {
  const swatches = useColorSwatches();
  // No data (still loading, lookup failed, or no color has a hex yet) keeps
  // the plain text labels.
  if (!swatches) return null;

  // A color with no hex yet (newly added in EP) keeps its row aligned with an
  // empty outlined circle rather than shifting its label left.
  const { background, light } = swatches[name] ?? { light: true };
  return (
    <span
      className={cn('facet-swatch', { light })}
      style={background ? { background } : undefined}
      data-testid='facet-swatch'
      aria-hidden
    />
  );
}

function FacetMenu(props: FacetMenuProps) {
  const {
    className = '',
    filterState,
    dropdownStateKey,
    stateKey,
    facets,
    title,
    open,
    onFilterOpen,
    onFacetClick,
    checkmarkComponent,
    lockedValues
  } = props;
  // Swatches are decorative, so the Color circles (and their fetch) wait for
  // the group's first open instead of delaying the page.
  const [hasOpened, setHasOpened] = useState(open);
  if (open && !hasOpened) setHasOpened(true);

  // Union locked values into the rendered list so a pinned value shows up even
  // when it has no facet count in the current search response.
  const displayNames = Array.from(
    new Set([...(lockedValues ?? []), ...Object.keys(facets ?? {})])
  ).sort();

  return (
    <div className={cn('FacetMenu', className)}>
      <div
        className='facet-row-label cursor'
        role='button'
        onClick={() => {
          onFilterOpen(dropdownStateKey);
        }}
      >
        <Text
          className='menu-label'
          uppercase
        >
          {title}
        </Text>
        <Image
          className={cn('chevron-down-icon cursor', {
            flip: open
          })}
          src={ChevronDown}
          height={16}
          width={16}
          alt={`${title} Filter`}
        />
      </div>
      <div className={cn('facet-row-content', { open })}>
        {displayNames.map((displayName, index) => {
          const isLocked = !!lockedValues?.includes(displayName);
          const isChecked =
            isLocked || filterState[stateKey].includes(displayName);
          return (
            <div
              className={cn('facet-row-item', {
                cursor: !isLocked,
                locked: isLocked
              })}
              key={`${displayName}_${index}`}
              {...(isLocked
                ? { 'aria-disabled': true, 'data-testid': 'locked-facet' }
                : {})}
            >
              <Text
                onClick={
                  isLocked
                    ? undefined
                    : () => {
                        const filterCopy = createFilterStateCopy(filterState);
                        filterCopy[stateKey] = filterCopy[stateKey].includes(
                          displayName
                        )
                          ? filterCopy[stateKey].filter(
                              (v) => v !== displayName
                            )
                          : [...filterCopy[stateKey], displayName];
                        filterCopy.pageNo = 1;
                        onFacetClick(filterCopy);
                      }
                }
              >
                {stateKey === 'color' && hasOpened && (
                  <ColorSwatch name={displayName} />
                )}
                {displayName}
              </Text>
              {isChecked && checkmarkComponent}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default FacetMenu;
